import { useEffect, useState, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { MDXProvider } from "@mdx-js/react";
import { DocsSidebar } from "./components/DocsSidebar";
import { DocsPageNavigation } from "./components/DocsPageNavigation";
import { TableOfContents } from "./components/TableOfContents";
import { mdxComponents } from "./components/MDXComponents";
import { getPageBySlug, getAdjacentPages } from "./docsStructure";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Use Vite's import.meta.glob to pre-load all MDX files
const mdxModules = import.meta.glob("./content/**/*.mdx");

export default function DocsPage() {
  const location = useLocation();
  const [MDXContent, setMDXContent] = useState<React.ComponentType | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Extract slug from path: /docs/ → '' (intro), /docs/quickstart → 'quickstart'
  const slug = location.pathname.replace(/^\/docs\/?/, "");

  useEffect(() => {
    const loadMDX = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const page = getPageBySlug(slug);

        if (!page) {
          setError("Page not found");
          setIsLoading(false);
          return;
        }

        // Construct the import path
        const importPath = `./content/${page.file}.mdx`;

        // Get the loader function for this path
        const loader = mdxModules[importPath];

        if (!loader) {
          console.error("MDX file not found:", importPath);
          console.error("Available paths:", Object.keys(mdxModules));
          setError("Documentation page not found");
          setIsLoading(false);
          return;
        }

        // Load the module
        const module = (await loader()) as { default: React.ComponentType };
        setMDXContent(() => module.default);
      } catch (err) {
        console.error("Error loading MDX:", err);
        setError("Failed to load documentation page");
      } finally {
        setIsLoading(false);
      }
    };

    loadMDX();
  }, [slug]);

  const adjacentPages = getAdjacentPages(slug);

  return (
    <div className="md:flex md:gap-8">
      <DocsSidebar />

      <div className="flex-1 min-w-0">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!isLoading && !error && MDXContent && (
          <div className="relative max-w-4xl">
            <TableOfContents />
            <article
              className="prose max-w-none text-foreground
              prose-headings:text-foreground prose-headings:font-bold prose-headings:tracking-tight
              prose-h1:text-4xl prose-h1:mb-6
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:border-b prose-h2:border-border prose-h2:pb-2
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground prose-p:text-base prose-p:leading-7
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground prose-strong:font-semibold
              prose-code:bg-muted prose-code:text-foreground prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono
              prose-code:before:content-none prose-code:after:content-none
              prose-pre:bg-muted prose-pre:text-foreground prose-pre:border prose-pre:border-border prose-pre:rounded-lg prose-pre:[&>code]:block prose-pre:[&>code]:p-0
              prose-img:rounded-lg prose-img:border prose-img:border-border
              prose-li:text-foreground prose-ul:my-4 prose-ol:my-4 prose-li:my-1
              prose-table:border prose-table:border-border prose-table:rounded-lg prose-table:overflow-hidden
              prose-th:bg-accent prose-th:text-foreground prose-th:p-3 prose-th:font-semibold
              prose-td:text-foreground prose-td:p-3 prose-td:border-t prose-td:border-border
              prose-blockquote:text-muted-foreground prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic"
            >
              <MDXProvider components={mdxComponents}>
                <Suspense
                  fallback={<Loader2 className="h-6 w-6 animate-spin" />}
                >
                  <MDXContent />
                </Suspense>
              </MDXProvider>
            </article>

            <DocsPageNavigation
              prev={adjacentPages.prev}
              next={adjacentPages.next}
            />
          </div>
        )}
      </div>
    </div>
  );
}
